import { Box } from "@mui/joy";
import FooterBrand from "../../common/FooterBrand";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import { useAuth } from "../../../contexts/api";
import { useAlert } from "../../../contexts/app/notificationToast";
import type { CompanyRole } from "../../../api/auth/Auth.typed";
import { SBusiness, SPerson, SLogout } from "../../../styles/StyledMUI";
import { ctaAccentColor } from "../../../styles/globalStyles";
import { userDisplayName } from "../../../utils/userHelper";

const FOOTER_FONT_SIZE = 14;
const FOOTER_ICON_SIZE = 20;

export default function MenuFooter() {
  const { user, userCompanies, openCompanySwitchModal, logoutCallback } = useAuth();
  const { showAlert } = useAlert();

  const currentCompanyId =
    user?.Role?.company_id ??
    user?.company_id ??
    (user?.Role as { CompanyId?: number } | undefined)?.CompanyId;

  const currentCompanyName =
    currentCompanyId != null && (userCompanies?.length ?? 0) > 0
      ? (userCompanies?.find((cr: CompanyRole) => cr.company?.id === currentCompanyId)?.company
          ?.name ?? "Profil actif")
      : (userCompanies?.length ?? 0) > 0
        ? (userCompanies![0]?.company?.name ?? "Profil actif")
        : currentCompanyId != null
          ? "Profil actif"
          : null;

  const canSwitchCompany = (userCompanies?.length ?? 0) > 1;

  const handleLogout = async () => {
    try {
      await logoutCallback();
      showAlert("A bientôt !", "success");
    } catch (err) {
      if (err instanceof Error) showAlert(err.message, "danger");
    }
  };

  const showCompany = currentCompanyName != null || canSwitchCompany;

  return (
    <Box
      component="footer"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        paddingX: { xs: 2, sm: 3 },
        width: "100%",
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          flexWrap: "wrap",
          "& > div svg": { cursor: "default", pointerEvents: "none" },
          "& > div svg:hover": { color: "inherit" },
        }}
      >
        {showCompany && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: canSwitchCompany ? "pointer" : "default",
              "&:hover": canSwitchCompany ? { opacity: 0.9 } : {},
            }}
            onClick={canSwitchCompany ? openCompanySwitchModal : undefined}
            role={canSwitchCompany ? "button" : undefined}
            aria-label={canSwitchCompany ? "Changer de société" : undefined}
          >
            <SBusiness sx={{ fontSize: FOOTER_ICON_SIZE }} />
            <DarkTypography sx={{ fontSize: FOOTER_FONT_SIZE, color: ctaAccentColor }}>
              {currentCompanyName ?? "Société"}
            </DarkTypography>
          </Box>
        )}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SPerson sx={{ fontSize: FOOTER_ICON_SIZE }} />
          <DarkTypography sx={{ fontSize: FOOTER_FONT_SIZE }}>
            {user ? userDisplayName(user) || "Utilisateur" : "Utilisateur"}
          </DarkTypography>
        </Box>

        <IconButtonCTA
          aria-label="Déconnexion"
          size="sm"
          variant="plain"
          onClick={handleLogout}
          sx={{
            width: FOOTER_ICON_SIZE,
            height: FOOTER_ICON_SIZE,
            minWidth: FOOTER_ICON_SIZE,
            minHeight: FOOTER_ICON_SIZE,
          }}
        >
          <SLogout sx={{ fontSize: FOOTER_ICON_SIZE }} />
        </IconButtonCTA>
      </Box>
      <FooterBrand showChromeNote />
    </Box>
  );
}
